import { getAffairOwnerName, formatAffairAmount, formatAffairDate, formatAffairStage, getAffairPartnerName } from '../affairFormatters';
import ScorePanel from '../../components/ScorePanel';

const read = (source, ...keys) => keys.map((key) => source?.[key]).find((value) => value !== undefined && value !== null && value !== '');
const display = (value, fallback = 'Non renseigné') => value === undefined || value === null || value === '' ? fallback : value;

const relatedName = (affair, relationKeys, nameKeys) => {
  const relation = read(affair, ...relationKeys);
  return relation ? read(relation, ...nameKeys) : null;
};
const personName = (person) => person && ([person.firstName || person.first_name, person.lastName || person.last_name].filter(Boolean).join(' ') || read(person, 'displayName', 'label', 'name', 'email'));

const Field = ({ label, value }) => (
  <div className="affair-overview__field">
    <dt>{label}</dt>
    <dd>{display(value)}</dd>
  </div>
);

const Section = ({ children, title }) => (
  <section className="affair-overview__section">
    <h2>{title}</h2>
    <dl className="affair-overview__fields">{children}</dl>
  </section>
);

const BOOL_LABEL = (value) => value === true ? 'Oui' : value === false ? 'Non' : null;
const QUALITY = { good: 'Bonne', partial: 'Partielle', poor: 'Faible' };
const COMPLEXITY = { simple: 'Simple', standard: 'Standard', complex: 'Complexe', very_complex: 'Très complexe' };
const DOC_REQ = { standard: 'Standard', high: 'Renforcée', very_high: 'Très forte' };
const technicalCompleteness = (affair) => {
  const tq = affair.requirements?.technicalQualification || {};
  const values = [affair.projectType, affair.buildingType, affair.workType, affair.surface, tq.levelCount, tq.occupiedSite, tq.inputDataQuality, tq.architecturalComplexity, tq.constructionPhases, tq.electricalBoardsCount, tq.earthingSystem, Array.isArray(tq.selectedLots) && tq.selectedLots.length ? tq.selectedLots : null, Array.isArray(tq.selectedPhases) && tq.selectedPhases.length ? tq.selectedPhases : null];
  const filled = values.filter((v) => v !== undefined && v !== null && v !== '').length;
  return Math.round((filled / values.length) * 100);
};

const formatSurface = (surface) => surface === undefined || surface === null || surface === '' ? null : `${Number(surface).toLocaleString('fr-FR')} m²`;
const getLots = (affair) => {
  const lots = affair.lots || affair.AffairLots || affair.requirements?.technicalQualification?.selectedLots || affair.requirements?.lots || affair.requirements?.selectedLots || [];
  if (!Array.isArray(lots)) return [];
  return lots.map((lot) => typeof lot === 'string' ? lot : lot.label || lot.code).filter(Boolean);
};

export default function AffairOverview({ affair, affairScore }) {
  const lots = getLots(affair);
  const agencyName = relatedName(affair, ['Agency', 'agency'], ['socialReason', 'social_reason', 'name']);
  const ownerName = getAffairOwnerName(affair);
  const tq = affair.requirements?.technicalQualification || {};
  const completeness = technicalCompleteness(affair);
  const technicalLeadName = personName(read(affair, 'TechnicalLead', 'technicalLead')) || relatedName(affair, ['TechnicalLead', 'technicalLead'], ['displayName', 'label', 'name']);

  return (
    <div className="affair-overview">
      <section className="affair-overview__section affair-overview__section--wide"><h2>Évaluation de l’Affaire</h2><p>La probabilité ({Number(affair.probabilityPercent || 0)} %) dépend du pipeline. Le Score d’Affaire mesure séparément la qualité du dossier.</p><ScorePanel title="Score d’Affaire" score={affairScore} /></section>
      <Section title="Informations de l'affaire">
        <Field label="Nom" value={affair.name} />
        <Field label="Code" value={affair.code} />
        <Field label="Partenaire" value={getAffairPartnerName(affair)} />
        <Field label="Agence" value={agencyName || affair.agencyId} />
        <Field label="Étape" value={formatAffairStage(affair.stage)} />
        <Field label="Probabilité calculée" value={`${Number(affair.probabilityPercent || 0).toLocaleString('fr-FR')} %`} />
        <Field label="Responsable d'affaire" value={ownerName} />
        <Field label="Responsable études" value={technicalLeadName || (affair.technicalLeadUserId ? `Utilisateur #${affair.technicalLeadUserId}` : null)} />
        <Field label="Priorité" value={affair.priority} />
        <Field label="Montant estimé" value={formatAffairAmount(affair.estimatedAmount)} />
        <Field label="Date limite de réponse" value={formatAffairDate(affair.responseDeadline)} />
        <Field label="Date de démarrage estimée" value={formatAffairDate(affair.desiredStartDate)} />
      </Section>

      <Section title="Projet envisagé">
        <Field label="Domaine du projet" value={affair.projectType} />
        <Field label="Type de bâtiment" value={affair.buildingType} />
        <Field label="Nature des travaux" value={affair.workType} />
        <Field label="Surface" value={formatSurface(affair.surface)} />
        <Field label="Bâtiments" value={tq.buildingCount} />
        <Field label="Niveaux" value={tq.levelCount} />
        <Field label="Niveaux / zones concernés" value={tq.concernedLevels} />
        <Field label="Site occupé" value={BOOL_LABEL(tq.occupiedSite)} />
        <Field label="Lots / périmètres" value={Array.isArray(tq.selectedLots) && tq.selectedLots.length ? tq.selectedLots.join(', ') : null} />
        <Field label="Phases d’études" value={Array.isArray(tq.selectedPhases) && tq.selectedPhases.length ? tq.selectedPhases.join(', ') : null} />
        <Field label="Ville" value={affair.city} />
        <Field label="Adresse" value={[affair.addressLine1, affair.addressLine2, affair.postalCode, affair.city].filter(Boolean).join(', ')} />
      </Section>

      <Section title={`Qualification technique · ${completeness} % complétée`}>
        <Field label="Qualité des données d’entrée" value={QUALITY[tq.inputDataQuality]} />
        <Field label="Complexité architecturale" value={COMPLEXITY[tq.architecturalComplexity]} />
        <Field label="Phases chantier" value={tq.constructionPhases} />
        <Field label="BIM demandé" value={BOOL_LABEL(tq.bimRequired)} />
        <Field label="Tableaux CFO" value={tq.electricalBoardsCount} />
        <Field label="Départs CFO estimés" value={tq.electricalDeparturesCount} />
        <Field label="Régime de neutre" value={tq.earthingSystem} />
        <Field label="Baies CFA" value={tq.cfaRacksCount} />
        <Field label="Points CFA estimés" value={tq.cfaPointsCount} />
        <Field label="Systèmes CFA" value={tq.cfaSystemsCount} />
        <Field label="Équipements SSI estimés" value={tq.ssiDevicesCount} />
        <Field label="Zones SSI" value={tq.ssiZonesCount} />
        <Field label="Régime réglementaire SSI" value={tq.ssiEstablishmentRegime} />
        <Field label="Type / catégorie ERP" value={tq.ssiEstablishmentRegime === 'ERP' ? [tq.erpType ? `Type ${tq.erpType}` : null, tq.erpCategory ? `${tq.erpCategory}${tq.erpCategory === '1' ? 're' : 'e'} catégorie` : null].filter(Boolean).join(' · ') : null} />
        <Field label="Locaux à sommeil" value={BOOL_LABEL(tq.sleepingPremises)} />
        <Field label="Catégorie SSI" value={tq.ssiCategory ? `Catégorie ${tq.ssiCategory}` : null} />
        <Field label="Modules PV" value={tq.pvModulesCount} />
        <Field label="Strings PV" value={tq.pvStringsCount} />
        <Field label="Puissance PV" value={tq.pvPowerKwp == null ? null : `${Number(tq.pvPowerKwp).toLocaleString('fr-FR')} kWc`} />
        <Field label="Locaux (approx.)" value={tq.roomCount} />
        <Field label="Variantes / options" value={tq.variantsCount} />
        <Field label="Exigence documentaire" value={DOC_REQ[tq.documentaryRequirement]} />
        <Field label="Relevé de site nécessaire" value={BOOL_LABEL(tq.siteSurveyRequired)} />
      </Section>

      <section className="affair-overview__section affair-overview__section--wide">
        <h2>Description</h2>
        <p className="affair-overview__description">{display(affair.description)}</p>
        <h3>Lots</h3>
        {lots.length > 0
          ? <div className="affair-overview__lots">{lots.map((lot) => <span key={lot}>{lot}</span>)}</div>
          : <p className="affair-overview__empty">Aucun lot renseigné.</p>}
      </section>
    </div>
  );
}
